/**
 * Button Component
 *
 * A flexible, reusable button component with support for multiple variants, icons,
 * disabled state, and custom styling. Suitable for forms, modals, and general UI actions.
 *
 * Features:
 * - Supports multiple visual variants: primary, secondary, warning, success, error, info.
 * - Can display an optional icon next to the button title.
 * - Handles `onClick` and `onKeyDown` events.
 * - Supports disabling the button with visual feedback (opacity).
 * - Accepts additional CSS classes via `extraClassName` for further customization.
 * - Allows passing a ref to the underlying button element (`ButtonRef`).
 *
 * Props (ButtonProps):
 * @param {string} title - The button text.
 * @param {React.ButtonHTMLAttributes<HTMLButtonElement>['type']} type - Button type attribute (button, submit, reset).
 * @param {() => void} onClick - Function to execute when the button is clicked.
 * @param {string} icon - Optional URL of an icon to display inside the button.
 * @param {string} extraClassName - Optional additional CSS classes.
 * @param {string} variant - Visual variant of the button (primary, secondary, warning, success, error, info).
 * @param {boolean} disabled - Whether the button is disabled.
 * @param {(e: React.KeyboardEvent<HTMLButtonElement>) => void} onKeyDown - Optional keyboard event handler.
 * @param {React.Ref<HTMLButtonElement>} ButtonRef - Optional ref for the button element.
 *
 * Example Usage:
 * <Button
 *   title="Submit"
 *   variant="primary"
 *   onClick={handleSubmit}
 *   icon="/icons/send.svg"
 *   extraClassName="w-1/2"
 * />
 */

import { ButtonProps } from "../../types/GeneralComponents";

const Button: React.FC<ButtonProps> = ({
  title,
  type,
  onClick,
  icon,
  lottieIcon,
  extraClassName,
  variant,
  disabled,
  onKeyDown,
  ButtonRef,
}) => {
  const getVariantClasses = (variant: string | undefined) => {
    switch (variant) {
      case "primary":
        return "bg-primaryMain text-white font-bold";
      case "secondary":
        return "bg-primaryLighter text-textColor font-semibold";
      case "warning":
        return "bg-warningMain text-white font-semibold";
      case "success":
        return "bg-successMain text-white font-semibold";
      case "error":
        return "bg-errorMain text-white font-semibold";
      case "info":
        return "bg-secondaryMain text-white font-semibold";
      case "alert":
        return "bg-secondaryLighter text-secondaryMain border !border-secondaryMain font-semibold";
      case "alert-outlined":
        return "bg-successLighter text-successMain border !border-successMain font-semibold";
      case "outlined":
        return "bg-white text-headingTextColor py-4  font-semibold";
      default:
        return "bg-primaryLighter text-primaryMain font-normal";
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      type={type}
      onKeyDown={onKeyDown}
      ref={ButtonRef}
      className={`${extraClassName} flex w-full items-center justify-center gap-2 rounded-lg px-8 py-3 capitalize focus:outline-none ${getVariantClasses(
        variant,
      )}
    
       
       `}
    >
      {icon &&
        (typeof icon === "string" ? (
          <img src={icon} alt="icon" loading="lazy" />
        ) : (
          icon
        ))}

      {title}
    </button>
  );
};

export default Button;
